/**
 * The single merged UBP-Q4 context. Holds the wizard flow state (from the
 * flow source) together with the unified product selection + per-product
 * inputs and the CCP pricing data / derived calculation (from the calc
 * source). A product is "selected" iff it is present in `products`.
 */
import {
	createContext,
	Dispatch,
	PropsWithChildren,
	SetStateAction,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';

import type { CurrencyISOCode } from '@/components/wpl/pricing/src/price-finder/constants';
import { useCCPPriceFinderData } from '@/contentful/product-catalog';
import type { APICallStatus } from '@/contentful/product-catalog';

import {
	PRODUCT_CATALOG_DELIVERY_KEY,
	PRODUCT_CATALOG_SPACE,
} from '../../../../uniform/components/pricing-calculator/src/client/contentful-clients';
import {
	calculateMonthlyPerUnitPrices,
	collectAllRegisteredSkus,
	type CalculateProductsResult,
} from '../calculator/calculate-monthly-per-unit-prices';
import {
	getProductPricingConfig,
	productPricingConfigs,
	productPricingConfigKeys,
} from '../calculator/product-pricing-configs';
import { resolveBillingCycle } from '../calculator/resolve-billing-cycle';

import { OrgUserCountError } from './validation';

import type { Meter, Product, Step, UsageType } from './types';
import type { CcpPricingData } from '../../../../uniform/components/pricing-calculator/src/types';
import type { ProductKey } from '../calculator/types';

export interface UbpContextValue {
	// wizard flow
	step: Step;
	meter: Meter;
	usageType: UsageType;
	usage: number;
	orgUserCount?: number;
	// unified product selection + inputs
	products: Product[];
	setProducts: Dispatch<SetStateAction<Product[]>>;
	productTabSelectionWarningShown: boolean;
	setProductTabSelectionWarningShown: Dispatch<SetStateAction<boolean>>;
	// pricing data + derived calculation
	ccpPricingData: CcpPricingData;
	ccpStatus: APICallStatus;
	currencyISOCode: CurrencyISOCode;
	localeISOCode: string;
	calculation: CalculateProductsResult;
	// flow setters
	setStep: Dispatch<SetStateAction<Step>>;
	setMeter: Dispatch<SetStateAction<Meter>>;
	setUsageType: Dispatch<SetStateAction<UsageType>>;
	setUsage: Dispatch<SetStateAction<number>>;
	setOrgUserCount: Dispatch<SetStateAction<number | undefined>>;
	// errors
	orgUserCountError?: OrgUserCountError;
	setOrgUserCountError: Dispatch<SetStateAction<OrgUserCountError | undefined>>;
	/**
	 * Reset the entire calculator to its initial (blank) state — step
	 * jumps to 'usage', meter/usageType/usage/orgUserCount/products/errors
	 * all revert to defaults. Used by the Restart button on Estimate
	 * and Summary steps (GWP-12674).
	 */
	resetAll: () => void;
}

const UbpContext = createContext<UbpContextValue | undefined>(undefined);

export const useUbpContext = (): UbpContextValue => {
	const ctx = useContext(UbpContext);
	if (!ctx) {
		throw new Error('useUbpContext must be used within a UbpContextProvider');
	}
	return ctx;
};

/** Build a product seeded entirely from its registry defaults. */
export const buildDefaultProduct = (productKey: ProductKey): Product => {
	const def = getProductPricingConfig(productKey);
	return {
		productKey,
		inputs: {
			edition: def.defaults.edition,
			unitCount: def.defaults.unitCount,
			billingCycle: resolveBillingCycle(productKey, def.defaults.edition),
		},
	};
};

export interface UbpInitialValues {
	step: Step;
	meter: Meter;
	usageType: UsageType;
	usage: number;
	orgUserCount: number;
	products: Product[];
}

export type UbpContextProviderProps = PropsWithChildren<{
	initialValues?: Partial<UbpInitialValues>;
	currencyISOCode?: CurrencyISOCode;
	localeISOCode?: string;
}>;

const DEFAULT_STEP: Step = 'usage';
const DEFAULT_METER: Meter = 'rovoCredits';
const DEFAULT_USAGE_TYPE: UsageType = 'adopting';
const DEFAULT_USAGE = 0;

export const UbpContextProvider = (props: UbpContextProviderProps) => {
	const {
		children,
		initialValues: {
			step: initialStep = DEFAULT_STEP,
			meter: initialMeter = DEFAULT_METER,
			usageType: initialUsageType = DEFAULT_USAGE_TYPE,
			usage: initialUsage = DEFAULT_USAGE,
			orgUserCount: initialOrgUserCount,
			products: initialProducts = [],
		} = {},
		currencyISOCode = 'USD',
		localeISOCode = 'en-US',
	} = props;
	const [step, setStep] = useState<Step>(initialStep);
	const [meter, setMeter] = useState<Meter>(initialMeter);
	const [usageType, setUsageType] = useState<UsageType>(initialUsageType);
	const [usage, setUsage] = useState<number>(initialUsage);
	const [orgUserCount, setOrgUserCount] = useState<number | undefined>(initialOrgUserCount);
	const [orgUserCountError, setOrgUserCountError] = useState<OrgUserCountError | undefined>(
		undefined,
	);
	const [products, setProducts] = useState<Product[]>(initialProducts);
	const [productTabSelectionWarningShown, setProductTabSelectionWarningShown] =
		useState<boolean>(false);

	// Fetch CCP data for the union of every registered product's SKUs once,
	// so selecting/deselecting products never triggers a re-fetch.
	const allSkus = useMemo(
		() => collectAllRegisteredSkus(productPricingConfigKeys.map((k) => productPricingConfigs[k])),
		[],
	);

	const {
		fetchData,
		status: ccpStatus,
		prices,
	} = useCCPPriceFinderData(
		allSkus,
		String(PRODUCT_CATALOG_DELIVERY_KEY),
		String(PRODUCT_CATALOG_SPACE),
	);

	useEffect(() => {
		void fetchData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const ccpPricingData = (prices ?? {}) as CcpPricingData;

	const calculation = useMemo(
		() =>
			calculateMonthlyPerUnitPrices({
				products,
				ccpPricingData,
				currencyISOCode,
				localeISOCode,
			}),
		[products, ccpPricingData, currencyISOCode, localeISOCode],
	);

	/**
	 * Restart the calculator flow. Wipes every wizard state field back
	 * to its default. Kept as a plain function (not memoized) — it's
	 * called at most once per user gesture and never appears in a
	 * hook dependency array, so referential stability doesn't matter.
	 */
	const resetAll = (): void => {
		setStep(DEFAULT_STEP);
		setMeter(DEFAULT_METER);
		setUsageType(DEFAULT_USAGE_TYPE);
		setUsage(DEFAULT_USAGE);
		setOrgUserCount(undefined);
		setOrgUserCountError(undefined);
		setProducts([]);
		setProductTabSelectionWarningShown(false);
	};

	const value: UbpContextValue = {
		step,
		meter,
		usageType,
		usage,
		orgUserCount,
		products,
		setProducts,
		productTabSelectionWarningShown,
		setProductTabSelectionWarningShown,
		ccpPricingData,
		ccpStatus,
		currencyISOCode,
		localeISOCode,
		calculation,
		setStep,
		setMeter,
		setUsageType,
		setUsage,
		setOrgUserCount,
		orgUserCountError,
		setOrgUserCountError,
		resetAll,
	};

	return <UbpContext.Provider value={value}>{children}</UbpContext.Provider>;
};
